import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { DiscountCode } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteDiscountCode } from '../hooks/use-discount-mutations';
import { codeDeleteErrorKey } from '../schemas/discount-rules';

export interface DeleteCodeDialogProps {
  storeId: string;
  discountId: string;
  code: DiscountCode | null;
  onOpenChange: (open: boolean) => void;
}

/** Confirms deleting one unused code; a refusal explains what to do instead. */
export function DeleteCodeDialog({
  storeId,
  discountId,
  code,
  onOpenChange,
}: DeleteCodeDialogProps) {
  const { t } = useTranslation(['discounts', 'common', 'errors']);
  const remove = useDeleteDiscountCode(storeId, discountId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;
  const errorKey = codeDeleteErrorKey(remove.error);

  return (
    <AlertDialog
      open={code !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('discounts:codes.delete.title', { code: code?.code ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('discounts:codes.delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {errorKey ? t(errorKey) : message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending || remove.isError}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (code) remove.mutate(code.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {remove.isPending
              ? t('discounts:codes.delete.submitting')
              : t('discounts:codes.delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
